<template>
<div data-component="ServicesPage.vue" class="services-container | max-w-4xl mx-auto">
	<Loader v-show="isLoading"/>

	<div v-show="!isLoading" class="a-loaded">
		<div>
			<!-- 📃 OKNA DIALOGOWE -->

			<!-- Dialog: Czy usunąć kategorię? -->
			<PromptDialog
				:modelValue="Boolean($route.query.removeGroupModalVisible)"
				:historyMode="false"
				:title="`Na pewno chcesz usunąć ${groupsToRemove && groupsToRemove.length > 1 ? Format.PluralForm(groupsToRemove.length, ['usługę', 'usługi', 'usług'], true) : 'tę usługę'}?`"
				body="Nie będziesz już dostawać zleceń na tę usługę, a wszystkie jej ustawienia zostaną utracone"
				:dismiss-text="`Nie, nie usuwaj  ${groupsToRemove && groupsToRemove.length > 1 ? 'usług' : 'usługi'}`"
				:confirm-text="`Tak, usuń ${groupsToRemove && groupsToRemove.length > 1 ? 'usługi' : 'usługę'}`"
				@confirmed="removeGroups(groupsToRemove);"
				@dismissed="cancelRemoveGroupModal"
			/>

			<!-- Dialog: Czy usunąć usługę? -->
			<PromptDialog
				:modelValue="Boolean($route.query.removeModalVisible)"
				:historyMode="false"
				:title="`Na pewno chcesz usunąć ${filterToRemove.type === 'quick' ? 'ten Szybki Kontakt' : 'to Polecanie'}?`"
				body="Nie będziesz już dostawać zleceń na tę usługę, a wszystkie jej ustawienia zostaną utracone"
				:dismiss-text="`Nie, nie usuwaj  ${filterToRemove.type === 'quick' ? 'tego Szybkiego Kontaktu' : 'tego Polecania'}`"
				:confirm-text="`Tak, usuń ${filterToRemove.type === 'quick' ? 'ten Szybki Kontakt' : 'to Polecanie'}`"
				@confirmed="removeFilter(filterToRemove);"
				@dismissed="cancelRemoveModal"
			/>

			<!-- Dialog: Czy zapisać zmiany? -->
			<PromptDialog
				v-model="displaySaveChanges"
				:history-mode="false"
				title="Czy chcesz zapisać zmiany?"
				confirm-text="Tak, zapisz zmiany"
				dismiss-text="Nie, wyjdź bez zapisywania"
				@confirmed="onSaveButtonClicked({ source:'dialog', saveForAllLocations: false })"
				@dismissed="backWithNoSaving(true, $event)"
			/>

			<div class="transition-opacity" :class="{ 'opacity-50 pointer-events-none': !isSavingCompleted }">
				<FilterList
					v-if="!currentPage"
					:servicesObject="services"
					:itemType="itemType"
					:processResult="processResult"
					:companyName="companyName"
					:apiNamespace="apiNamespace"
					@process="process"
					@add-new-groups="goToCreate"
					@group-limit-recalculate="groupLimitRecalculate"
					@send-analytics-event="sendAnalyticsEvent"
					@edit-locations="editLocations"
					@edit-matching="editMatching"
					@get-item-type="getItemType"
					@on-selected-filters-change="onSelectedFiltersChange"
					@recalculate-potential-matches="recalculatePotentialMatches"
				/>

				<FilterDetails
					v-if="currentPage === 'Details'"
					:servicesObject="services"
					:apiNamespace="apiNamespace"
					@edit-matching="editMatching"
					@send-analytics-event="sendAnalyticsEvent"
					@save-filter="saveFilter"
					@edit-locations="editLocations"
					@save-button-clicked="onSaveButtonClicked"
					@recalculate-potential-matches="recalculatePotentialMatches"
				/>

				<FilterPricing
					v-if="currentPage === 'Pricing'"
					ref="FilterPricing"
					:servicesObject="services"
					:apiNamespace="apiNamespace"
					:categoryId="pricingCategoryId"
					@save-button-clicked="onSaveButtonClicked"
					@send-analytics-event="sendAnalyticsEvent"
				/>

				<FilterCreate
					v-if="currentPage === 'Create'"
					ref="filterCreate"
					:servicesObject="services"
					:multi="isAdminModule"
					:apiNamespace="apiNamespace"
					:filter="filterTemplate"
					@send-analytics-event="sendAnalyticsEvent"
					@filter-create="onFilterCreate"
					@filter-create-template="getFilterTemplate"
				/>
			</div>
		</div>
	</div>

	<RecommendationFeedbackModal
		v-if="isRecommendationFeedbackModalVisible"
		:servicesObject="services"
		:api-namespace="apiNamespace"
	/>

	<TenPercentDiscountConfirmationModal
		v-if="visibilityTenPercentDiscountConfirmationModal"
	/>
</div>
</template>

<script lang="ts">
import { defineAsyncComponent } from 'vue';
import {
	mapState, mapStores, mapWritableState, mapActions,
} from 'pinia';
import { useServicesStore } from '../../../stores/services';
import { useAppStore } from '../../../stores/app';
import { useCustomerProfileViewStore } from '../../../stores/customerProfileView';
import Filter, {
	FilterEntry, FilterBackend, AutomaticRecommendationsSettings, SizeFilter, MpoAnswer, MpoQuestion,
} from '../../../types/interfaces/services/Filter';
import Tree from '../../../types/interfaces/services/Tree';
import ContactPerson, { ContactPersonBackend } from '../../../types/interfaces/services/ContactPerson';
import {
	Services, ServicesUserActivityStats, ServicesStats,
} from '../../../types/interfaces/services/Services';
import GeoTreeLocation from '../../../types/interfaces/map/GeoTreeLocation';
import GeoTreeLocationItem from '../../../types/interfaces/map/GeoTreeLocationItem';
import FilterGroup from '../../../types/interfaces/services/FilterGroup';
import PromptDialog from '../../../globals/components/global/PromptDialog.vue';
import { useLocationStore } from '../../../stores/location';
import serverFetch from '../../../globals/ServerFetch';

/** Komponent odpowiedzialny za ekran zmiany oferowanych produktów i usług w sekcji profilowej Mojego Oferteo. */
export default {
	name: 'ServicesPage',
	components: {
		PromptDialog,
		FilterList: defineAsyncComponent(() => import('./FilterList.vue')),
		FilterDetails: defineAsyncComponent(() => import('./FilterDetails.vue')),
		FilterCreate: defineAsyncComponent(() => import('./FilterCreate.vue')),
		FilterPricing: defineAsyncComponent(() => import('./FilterPricing.vue')),
		RecommendationFeedbackModal: defineAsyncComponent(() => import('./Modals/RecommendationFeedbackModal.vue')),
		TenPercentDiscountConfirmationModal: defineAsyncComponent(() => import('./Modals/TenPercentDiscountConfirmationModal.vue')),
	},
	beforeRouteEnter(to, from, next) {
		to.query = {};

		next();
	},
	beforeRouteUpdate(to, from, next) {
		// Podczas dodawania filtru SK nie pytaj się o zapisanie zmian podczas przechodzenia,
		// ponieważ użytkownik może wtedy zapisać filtr - nawet jeżeli nie wyświetliliśmy mu
		// informacji o tym, że filtr został w ogóle dodany (no i można wtedy ustawić dowolną
		// cenę za kontakt).
		if (from.params.filterId === 'dodaj-sk') {
			this.isFilterDirty = false;
		}

		if (this.displaySaveChanges) {
			this.backWithNoSaving(false);
		}

		// Jeżeli filtr został zmodyfikowany, wyświetl pytanie o zapisanie zmian
		// w momencie, kiedy użytkownik chce się cofnąć.
		if (this.isFilterDirty
			&& to.name !== 'FilterDetailsQuickMatching'
			&& to.name !== 'FilterDetailsQuickLocation'
			&& to.name !== 'FilterDetailsAutomaticMatching'
			&& to.name !== 'FilterDetailsAutomaticLocation'
		) {
			this.displaySaveChanges = true;
		}

		if (this.displaySaveChanges) {
			next(false);

			return;
		}

		if (to.name === 'FilterDetailsAutomatic' || to.name === 'FilterDetailsQuick' || to.name === 'FilterDetailsMatching') {
			this.isEditLocations = false;
			this.isEditMatching = false;
		}

		if (to.name === 'FilterListItem') {
			this.isEditLocations = false;
			this.isEditMatching = false;
			this.isInsideFilterDetails = false;
			this.currentPage = '';
		}

		if (to.name === 'Services') {
			this.isInsideFilterDetails = false;
			this.isEditLocations = false;
			this.isEditMatching = false;
			this.selectedCategoryGroupId = 0;
			this.currentPage = '';
		}

		next();
	},
	data() {
		return {
			/**
			 * Główny obiekt usług
			 */
			services: {
				locations: {},
				tree: [] as Tree[],
				stats: {} as ServicesStats,
				userActivityStats: {
					userActivityLoaded: false,
					userActivityDays: 30,
					matchedRfps: 0,
					boughtRfps: 0,
					matchedInstantRfps: 0,
				} as ServicesUserActivityStats,
				customer: {
					extendedLimit: true, // isCustomerQualifiedForHigherFilterLimit
				},
				contactPersons: [] as ContactPerson[],
				operators: [] as ContactPerson[],
				selectedFilters: [] as Filter[],
			} as Services,

			/**
			 * Nazwa typu filtru do sklonowania
			 */
			itemType: '',

			/**
			 * Wynik funkcji `process` z przetworzonym na postać
			 * frontendową filtrem
			 */
			processResult: {} as Filter,

			/**
			 * Czy obecnie jest przeprowadzana operacja zapisu filtra?
			 */
			isSavingCompleted: true,

			/**
			 * Nazwa firmy dla której wyświetlone są filtry
			 */
			companyName: '',

			/**
			 * Szablon filtra potrzebny do ustawienia lokalizacji
			 * podczas dodawania wielu filtrów
			 */
			filterTemplate: undefined as Filter|undefined,
		};
	},
	computed: {
		...mapStores(useLocationStore),
		...mapState(useAppStore, [
			'isAdminModule',
		]),
		...mapState(useCustomerProfileViewStore, [
			'isInRecommendationPromotion',
			'isInApPromotionalCampaign',
		]),
		...mapState(useServicesStore, [
			'selectedFilterIndex',
			'customerId',
		]),
		...mapWritableState(useServicesStore, [
			'currentPage',
			'displaySaveChanges',
			'filterToRemove',
			'isEditMatching',
			'isEditLocations',
			'isFilterDirty',
			'isInsideFilterDetails',
			'groupsToRemove',
			'selectedCategoryGroupId',
			'isFreshCategory',
			'isNewCategory',
			'pricingCategoryId',
			'isRecommendationFeedbackModalVisible',
			'invalidStats',
			'isLoading',
			'visibilityTenPercentDiscountConfirmationModal',
			'potentialMatches',
		]),

		/**
		 * Obecnie edytowany filtr
		 */
		selectedFilter() : Filter {
			return this.services.selectedFilters[
				this.selectedFilterIndex || 0
			];
		},

		/**
		 * Znajduje filtr z najwyższym ID (czyli najprawdopodobniej ostatnio dodany).
		 */
		lastAddedFilter() {
			let categoryIndex = 0;
			let filterIndex = 0;
			let maxFilterId = 0;

			this.services.tree.forEach(({ filters }, cIndex: number) => {
				filters.forEach((filter: Filter, fIndex: number) => {
					if (filter.id > maxFilterId) {
						maxFilterId = filter.id;
						categoryIndex = cIndex;
						filterIndex = fIndex;
					}
				});
			});

			return this.services.tree[categoryIndex].filters[filterIndex];
		},

		/**
		 * Początek ścieżki do API.
		 */
		apiNamespace() {
			return this.isAdminModule ? 'admin' : 'myoferteo';
		},
	},
	mounted() {
		this.inApPromotionalCampaign();
		this.loadData();
		this.sendAnalyticsEvent('Main_Show', 'show');
	},
	methods: {
		...mapActions(useCustomerProfileViewStore, ['inApPromotionalCampaign']),
		...mapActions(useAppStore, ['hasFlag']),
		/**
		 * Dokonuje zapisu wielu filtrów na raz
		 * @param baseFilter Filtr, z którego będą pobierane informacje.
		 * @param ids Tablica identyfikatorów filtrów do zmiany
		 * @param mode Które dane mają zostać edytowane? `edit` - dopasowanie zleceń, `location` - lokalizacje
		 */
		async multiSaveFilter(baseFilter:Filter, ids:number[], mode:'edit'|'location') {
			for (const id of ids) {
				const rsvp = await serverFetch(
					`/${this.apiNamespace}/get-filter-details.ofer?idCustomer=${this.customerId}&matchingFilter.id=${id}`,
				) as { filter:FilterBackend[] };
				const processed = this.process({ filterDetails: rsvp.filter[0] });
				if (!processed) { continue; }

				if (mode === 'edit') {
					[
						'contractors',
						'investors',
						'subcontractors',
						'discounted',
						'individuals',
						'notConfirmedBySms',
					].forEach((key) => {
						processed.filterTypes[key] = baseFilter.filterTypes[key];
					});
				}

				if (mode === 'location' && processed.location && baseFilter.location) {
					processed.location.selected = baseFilter.location.selected;
				}

				await this.saveFilter(processed);
			}

			window.setTimeout(async () => {
				this.displaySaveChanges = false;
				this.isFilterDirty = false;
				this.currentPage = '';
				this.$router.back();
				await this.getServicesList();
			}, 1000);
		},

		/**
		 * Akcja wywoływana podczas kliknięcia przycisku zapisania zmian.
		 * Może ona być wywołana z poziomu szczegółów fitlra (dolny przycisk)
		 * lub z okna dialogowego: w tym przypadku należy cofnąć się dwukrotnie,
		 * aby pominąć wyświetlenie się tego okna.
		 * @param source Źródło kliknięcia przycisku.
		 */
		async onSaveButtonClicked({ source, saveForAllLocations } : {
			source:'details'|'dialog'|'all-locations'|'price'|'multi-edit'|'multi-location',
			saveForAllLocations: boolean,
		}) {
			const { selectedFilter } = this;

			// Edycja wielu filtrów na raz
			if (String(selectedFilter.id).indexOf(',') > -1) {
				if (source.indexOf('multi-') > -1) {
					await this.multiSaveFilter(selectedFilter, selectedFilter.id.split(','), source.split('-')[1]);
				}
				return;
			}

			// Zapisz filtr, jeżeli nie dotyczy to strony z cenami.
			const pricing = this.$refs.FilterPricing as any;

			if (!pricing) {
				// Jeżeli filtr nie ma ustawionych lokalizacji, wyświetl komunikat walidacyjny.
				const noLocationsSelected = selectedFilter?.location?.selected.length === 0;
				if (noLocationsSelected) {
					this.Toast.Show({ Message: 'Usługa musi mieć wybraną lokalizację', Style: 'fail' });
					return;
				}

				// W innym przypadku zapisz filtr.
				await this.saveFilter(selectedFilter, false, saveForAllLocations);
				setTimeout(() => { this.loadData(); }, 1000);
			} else {
				const pricingSave = await pricing.savePrice();

				if (!pricingSave) {
					this.displaySaveChanges = false;
					this.isFilterDirty = false;
					this.currentPage = '';

					this.$router.back();

					return;
				}
			}

			this.displaySaveChanges = false;
			this.isFilterDirty = false;
			this.currentPage = '';

			this.$router.back();

			// Wyślij analitykę
			// TODO: Rozróżnienie pomiędzy Edit_Details, Location, Matching, Price
			this.sendAnalyticsEvent('Edit_Save', 'edit_save');
		},

		/**
		 * Dodaje kategorie, a następnie przenosi użytkownika na widok jej szczegółów.
		 * @param categoryIds Tablica z identyfikatorami kategorii do wstawienia
		 * @param location    Tablica z identyfikatorami lokalizacji.
		 *                    Jeżeli jest pusta - weź ją z ostatnio dodanego filtra.
		 */
		async onFilterCreate(e: { categoryIds: number[], location: string[] }) {
			const { categoryIds, location } = e;

			// TODO: Czy nie było metody, która dodaje kilka filtrów na raz?
			for (const id of categoryIds) {
				const filter = await this.getFilterTemplate(id, location || [], false);
				if (filter) {
					await this.saveFilter(filter, true);
				}
			}

			window.setTimeout(async () => {
				this.currentPage = '';

				if (this.isAdminModule) {
					this.$router.push({ name: 'Services' });
				} else {
					// Przejdź do wybranego filtra
					[this.selectedCategoryGroupId] = categoryIds;
					this.isFreshCategory = true;

					this.$router.push({ name: 'FilterListItem', params: { categoryId: categoryIds[0] } });
				}
			}, 150);
		},

		/**
		 * Pobiera filtr z wartościami z poprzednio dodanego filtra, oraz przypisuje mu
		 * kategorię oraz ustawia lokalizacje.
		 * @param categoryId Kategoria do ustawienia
		 * @param location   Tablica z identyfikatorami lokalizacji.
		 *                   Jeżeli jest pusta - weź ją z ostatnio dodanego filtra.
		 * @param assign     Przypisz filtr do zmiennej `filterTemplate`
		 */
		async getFilterTemplate(categoryId = 1, location:string[] = [], assign = false) {
			const url = `/myoferteo/get-new-filter.ofer?matchingFilter.idCustomer=${this.customerId}&matchingFilter.categories=`;
			const rsvp = (await serverFetch(`${url}${categoryId}`)) as FilterBackend;
			const filter = this.process({ filterDetails: rsvp });
			if (filter) {
				filter.location = {
					selected: location.length ? location : rsvp.filterLocations.map((l) => l.idLocation) as string[],
				};
				this.editLocations({ filter, display: false });
			}
			if (assign) {
				this.filterTemplate = filter;
			}
			return filter;
		},

		/**
		 * Wysyła zdarzenie do analityki za pomocą GA
		 * oraz `log-myoferteo-action-stats`
		 * @param label Nazwa zadarzenia dla `log-myoferteo-action-stats`
		 * @param gtmLabel Nazwa zadarzenia dla GA
		 */
		sendAnalyticsEvent(label:string, gtmLabel:string) {
			if (this.isAdminModule) { return; }
			this.Analytics.Gtm(`myServices_${gtmLabel || label}`);
			serverFetch(
				`/myoferteo/konto-json/log-myoferteo-action-stats.ofer?step=ProfileServices&action=${label}`,
			);
		},

		/**
		 * Masowo ustawia lokalizacje wszystkich bezpłatnych filtrów.
		 */
		async saveLocationForAll(filter:Filter) {
			await serverFetch('/myoferteo/set-filter-locations.ofer', {
				idCustomer: this.customerId,
				filterLocsType: 'mgFltrTypeNotif',
				selectedLocs: filter.location?.selected,
			});
		},

		/**
		 * Pobiera drzewo lokalizacji wraz z ich graficzną reprezentacją
		 * z pliku `scripts/geo.js` lub, jeśli wcześniej jego zawartość
		 * została pobrana - z `localStorage`.
		 */
		async growTree() {
			const JSON_VERSION = 7;
			const JSON_PATH = `/scripts/geo.js?rev=${JSON_VERSION}`;
			const JSON_TREE_KEY = 'geotree';

			const treeFromLocalStorage = window.localStorage[JSON_TREE_KEY];

			if (treeFromLocalStorage) {
				// Aktualna wersja, ustaw ją.
				const { data, version } = JSON.parse(treeFromLocalStorage) as {
					data:Record<string, GeoTreeLocation>
					version:number,
				};
				const normalizedData = {} as Record<string, GeoTreeLocation>;

				// normalize current data to lowercase
				Object.entries(data).forEach((group) => {
					const [key, values] = group as [string, GeoTreeLocation];

					const normalizedValues = Object.entries(values).reduce((prev, current) => {
						const [valueKey, value] = current as [string, any];
						const valueToLowerCase = valueKey.toLowerCase();

						if (valueToLowerCase === 'items') {
							Object.entries(value).forEach((entry) => {
								const [keyItems, valueItems] = entry as [string, GeoTreeLocationItem];

								const normalizedItemsValues = Object.entries(valueItems).reduce((prevItems, currentItems) => {
									const [itemsValueKey, itemsValue] = currentItems as [string, any];
									const itemsValueToLowerCase = itemsValueKey.toLowerCase();

									return { ...prevItems, [itemsValueToLowerCase]: itemsValue };
								}, {});

								value[keyItems] = normalizedItemsValues;
							});
						}

						return { ...prev, [valueToLowerCase]: value };
					}, {});

					normalizedData[key] = normalizedValues as GeoTreeLocation;
				});

				if (version >= JSON_VERSION) {
					// this.tree = { ...normalizedData };
					return normalizedData;
				}
			}

			const getRootFromName = (name:string, tree:Record<string, GeoTreeLocation>) => {
				for (const root of Object.keys(tree)) {
					if (name.startsWith(root)) {
						return tree[root];
					}
				}
				return undefined;
			};

			// Nie ma aktualnej, pobierz.
			const locs = {
				Countries: this.locationStore.countries,
				Poland: this.locationStore.poland,
			};

			const tree = {} as Record<string, GeoTreeLocation>;
			const domestic = locs.Poland;

			domestic.forEach(({ Value, Label }: { Value: string, Label: string }) => {
				tree[Value] = {
					name: Label,
					selected: 0,
					items: {},
					expanded: false,
				};
			});

			const foreign = locs.Countries;

			tree.locationForeign = {
				name: 'Zagranica',
				selected: 0,
				items: {},
				expanded: false,
			};

			foreign.forEach(({ Value, Label } : { Value:string, Label:string }) => {
				if (Value !== 'locationPl') {
					tree.locationForeign.items[Value] = {
						name: Label,
						selected: false,
					};
				}
			});

			const rsvp = await fetch(JSON_PATH).then((data) => data.json()) as Record<string, {
				name:string,
				geojson:any,
				cities: { v:string, n:string, }[]
			}>;

			const set = (
				items:Record<string, GeoTreeLocationItem>,
				index:string,
				locationObject:GeoTreeLocationItem,
			) => {
				items[index] = locationObject;
			};

			// eslint-disable-next-line guard-for-in
			for (const index in rsvp) {
				const location = rsvp[index];

				const root = getRootFromName(index, tree);
				if (!root) { continue; }

				set(root.items, index, {
					name: location.name,
					selected: false,
					geo: location.geojson,
				});

				if (!location.cities) {
					continue;
				}

				location.cities.forEach((city, cityIndex:number) => {
					const cityRoot = getRootFromName(city.v, tree);
					if (!cityRoot) { return; }

					set(cityRoot.items, city.v, {
						name: city.n,
						selected: false,
						geo: {
							type: 'Polygon',
							coordinates: [
								location.geojson.coordinates[Number(cityIndex) + 1],
							],
						},
					});
				});
			}

			window.localStorage[JSON_TREE_KEY] = JSON.stringify({
				version: JSON_VERSION,
				data: tree,
			});

			return tree;
		},

		/**
		 * Rozdziela listę identyfikatorów kategorii na grupę krajów i powiatów.
		 * @param inputLocations Lista identyfikatorów kategorii
		 * @returns Obiekt zawierający kraje i powiaty
		 */
		splitLocationsIntoGroups(inputLocations?:string[]) {
			const countries:string[] = [];
			const locations:string[] = [];
			if (inputLocations) {
				inputLocations.forEach((location) => {
					(location.startsWith('locationPl') ? locations : countries)
						.push(location);
				});
			}
			return { countries, locations };
		},

		/**
		 * Przekształca listę pytań z MPO (Matchowanie Po Odpowiedziach) filtra
		 * na postać, którą backend przyjmie przy zapisie.
		 * @param filter Filtr dla którego ma być zwrócony obiekt z odpowiedziami.
		 */
		saveFilterMpo(filter:Filter) {
			const mpoResult = {} as Record<string, string>;
			const mpo = JSON.parse(JSON.stringify(filter.mpo)) as MpoQuestion[];

			if ((this.isAdminModule || filter.type === 'free') && mpo.length) {
				// this format is required due the backend... [*]

				mpo.forEach((mpoQuestion, index) => {
					Object.entries(mpoQuestion).forEach(([key, value]) => {
						if (key === 'id') {
							key = 'questionId';
						}

						if (key === 'optionsList') {
							key = 'answersDtoList';
						}

						if (key !== 'answersDtoList' && key !== 'id' && key !== 'questionId' && key !== 'questionSetId') {
							return;
						}

						if (key === 'answersDtoList') {
							let mpoArrayItemNewIndex = 0;

							mpoQuestion['optionsList'].forEach((mpoArrayItem:MpoAnswer) => {
								if (!mpoArrayItem.checked) {
									return;
								}

								Object.entries(mpoArrayItem).forEach(([mpoArrayItemKey, mpoArrayItemValue]) => {
									if (mpoArrayItemKey !== 'id' && mpoArrayItemKey !== 'questionId' && mpoArrayItemKey !== 'questionSetId') {
										return;
									}

									mpoResult[`mpo[${index}].${key}[${mpoArrayItemNewIndex}].${mpoArrayItemKey}`] = mpoArrayItemValue;

									mpoArrayItemNewIndex++;
								});
							});

							return;
						}

						mpoResult[`mpo[${index}].${key}`] = value;
					});
				});
			}

			return mpoResult;
		},

		async recalculatePotentialMatches(filter: Filter) {
			const url = '/myoferteo/get-matching-filter-potential-matches.ofer';
			const result: any = this.getFilterObject(filter);
			const rsvp = await serverFetch(url, result, 'POST') as { result: number };
			this.potentialMatches = typeof rsvp.result === 'undefined' ? -1 : rsvp.result;
		},

		getFilterObject(filter: Filter, fieldPrefix = 'matchingFilter.', allLocations = false) {
			const result:any = {};

			const operationTypeMethod = (filter.isActive ? 'A' : 'X');
			const operationType = filter.isInitiallyActive !== filter.isActive ? operationTypeMethod : '';

			const ageFilterMin = filter.filterTypes.age?.minValue;
			const ageFilterMax = filter.filterTypes.age?.maxValue;
			const sizeFilterMin = filter.filterTypes.size?.minValue;
			const sizeFilterMax = filter.filterTypes.size?.maxValue;

			if (filter.tsSuspendedEnd) {
				result.tsSuspendedEnd = filter.tsSuspendedEnd;
			}

			if (filter.contactPerson.contactPersonId) {
				result[`${fieldPrefix}idCustomerContactUser`] = filter.contactPerson.contactPersonId;
			}

			Object.entries({
				...this.saveFilterMpo(filter),
				id: filter.id,
				isActive: filter.isActive,
				categories: filter.category.categoryId,
				idCustomer: this.customerId,
				editLocations: true,
				forceLocations: false,
				updateAllLocations: allLocations || false,
				operationType,
				idType: this.getItemType({ type: filter.type, reverse: true }),
				idStatus: filter.isActive ? 'mgFltrStatActive' : 'mgFltrStatInactive',
				idNotificationMode: filter.filterTypes.frequency,
				standardRfps: filter.filterTypes.standardRfps,
				outletRfps: filter.filterTypes.discounted,
				companies: filter.filterTypes.companies,
				individuals: filter.filterTypes.individuals,
				contractors: true,
				investors: true,
				subcontractors: true,
				notificationWithOutlet: filter.filterTypes.discounted,
				notConfirmedSmsRfps: filter.filterTypes.notConfirmedBySms,
				smsNumber: filter.quickContactSmsNumber,
				contactPrice: filter.contactPrice,
				tsSuspendedEnd: filter.tsSuspendedEnd,
				...(filter.filterTypes.age
					? {
						usedFrom: ageFilterMin !== 0 ? ageFilterMin : -1,
						usedTo: ageFilterMax !== 0 ? ageFilterMax : -1,
					}
					: {}),
				...(filter.filterTypes.size
					? {
						sizeFrom: sizeFilterMin !== 0 ? sizeFilterMin : -1,
						sizeTo: sizeFilterMax !== 0 ? sizeFilterMax : -1,
					}
					: {}),
				...(filter.automaticRecommendations
					? {
						imWeeklyLimit: filter.automaticRecommendations.weeklyLimit,
						imSmsNotification: filter.automaticRecommendations.smsNumber.enabled,
					}
					: {}),
				...this.splitLocationsIntoGroups(filter.location?.selected),
			}).forEach(([k, v]) => {
				result[fieldPrefix + k] = v;
			});

			return result;
		},

		/**
		 * Aktualizuje lub dodaje nowy filtr po wcześniejszym przekształceniu go
		 * na formę zjadliwą dla backendu.
		 * @param filter Filtr do dodania lub aktualizacji
		 * @param createFilter Czy zamiast edycji istniejącego, dodać nowy filtr?
		 * @param allLocations Czy aktualizować wszystkie lokalizacjie?
		 */
		async saveFilter(filter: Filter, createFilter = false, allLocations = false) {
			if (!this.isSavingCompleted) {
				return;
			}

			this.isSavingCompleted = false;

			// Zabezpieczka, jakby się coś wysypało przy zapisie.
			setTimeout(() => { this.isSavingCompleted = true; }, 7500);

			const fieldPrefix = !createFilter ? 'matchingFilter.' : 'newFilters[0].';
			const result:any = this.getFilterObject(filter, fieldPrefix, allLocations);

			if (createFilter && this.isAdminModule) {
				this.isLoading = true;
			}

			const url = `/${this.apiNamespace}/${!createFilter ? 'edit' : 'add-new'}-filter.ofer`;

			const rsvp = await serverFetch(url, result, 'POST') as {
				result:string,
				filters:FilterBackend[],
			};

			if (rsvp.result !== 'ok' && !rsvp.filters) {
				console.error('Wystąpił błąd podczas dodawania kategorii.');
			}

			// Zawsze pobieraj nowe dane, inaczej licznik matchowań nie zostanie zaktualizowany.
			// Kiedyś było to tylko w momencie, kiedy dodawano lub klonowano nowy filtr (createFilter || filter.isClone).
			await this.loadData();

			this.Toast.Show({ Message: 'Zmiany zostały zapisane' });
			this.isSavingCompleted = true;

			// Wyświetl okno dialogowe czy włączyć nieaktywną usługę
			const filterGroup = this.services.tree.find((group) => filter.category.categoryId === group.categoryId);
			if (!this.isAdminModule && !filterGroup?.isActive) {
				window.setTimeout(() => {
					this.$router.push({ query: { enableServiceOnSaveModalVisible: 'true' } });
				}, 250);
			}
		},

		/**
		 * Przenosi użytkownika na ekran dopasowania filtra
		 */
		editMatching() {
			this.isEditMatching = true;
			this.isInsideFilterDetails = true;

			const routeName = this.selectedFilter.type === 'automatic' ? 'FilterDetailsAutomaticMatching' : 'FilterDetailsQuickMatching';

			this.$router.push({ name: routeName, params: { filterId: this.selectedFilter.id || (this.selectedFilter.type === 'quick' ? 'dodaj-sk' : 'dodaj-polecanie') } });
		},

		/**
		 * Przenosi użytkownika na ekran dopasowania lokalizacji filtra.
		 * Dodatkowo generuje drzewo lokalizacji dla danego filtra.
		 */
		editLocations({ filter = undefined, display = true } : { filter:Filter|undefined, display:boolean }) {
			const selectedFilter = filter || this.selectedFilter;
			if (!selectedFilter.location) { return; }

			const { selected } = selectedFilter.location;

			const locations = JSON.parse(
				JSON.stringify(this.services.locations, (key, value) => (key === 'geo' ? undefined : value)),
			);

			// eslint-disable-next-line guard-for-in
			for (const index in locations) {
				const selectedAll = selected.indexOf(index) > -1;
				locations[index].expanded = false;
				locations[index].selected = 0;

				if (selectedAll) {
					locations[index].selected = Object.keys(locations[index].items).length;
				}

				// eslint-disable-next-line guard-for-in
				for (const itemIndex in locations[index].items) {
					if (selectedAll) {
						locations[index].items[itemIndex].selected = true;

						continue;
					}

					if (selected.indexOf(itemIndex) > -1) {
						locations[index].items[itemIndex].selected = true;
						locations[index].selected++;
					}
				}
			}

			selectedFilter.locations = { ...locations };
			selectedFilter.location = { ...selectedFilter.location };

			if (display) {
				this.isEditLocations = true;
				this.isInsideFilterDetails = true;

				const routeName = this.selectedFilter.type === 'automatic' ? 'FilterDetailsAutomaticLocation' : 'FilterDetailsQuickLocation';

				this.$router.push({ name: routeName, params: { filterId: this.selectedFilter.id || (this.selectedFilter.type === 'quick' ? 'dodaj-sk' : 'dodaj-polecanie') } });
			}
		},

		/**
		 * Oblicza liczbę kategorii dla limitu posiadanych przez użytkownika filtrów.
		 * Wliczane są tylko aktywne filtry powiadomień. Na dodatek, jeżeli kategoria
		 * jest `isGeneraled` (np. wózki widłowe), policz tylko jej rodzica.
		 */
		groupLimitRecalculate() {
			let hasGroups = false;
			let count = 0;

			const groups:any = {};

			for (const group of this.services.tree) {
				const filter = group.filters.find(({ type }) => type === 'free');

				if (!group.isActive || !filter) {
					continue;
				}

				const category = { ...filter.category };
				let ignore = false;

				if (!category.isGeneraled) {
					if (groups[category.parentId]) {
						ignore = true;
						hasGroups = true;
					} else {
						groups[category.parentId] = true;
					}
				}

				if (!ignore) {
					count++;
				}
			}

			this.services.stats.hasGroupedCategories = hasGroups;
			this.services.stats.activeGroupCount = count;
		},

		/**
		 * Przekształca listę osób kontaktowych z postaci backendową na frontendową.
		 * @param contactPersons Tablica osób kontaktowych
		 */
		getContactPersons(contactPersons : ContactPersonBackend[]) : ContactPerson[] {
			return contactPersons.map((person) => ({
				id: person.id,
				fullName: person.fullName,
				phone: person.phone,
				email: person.login,
			}));
		},

		/**
		 * Przekształca listę Operatorów Oferteo z postaci backendową na frontendową.
		 * @param operators Tablica Operatorów Oferteo
		 */
		getOperators(operators: ContactPersonBackend[]): ContactPerson[] {
			return operators.map((operator) => ({
				id: operator.idCustomer,
				fullName: operator.fullName,
				phone: operator.phone,
				email: operator.login,
			}));
		},

		/**
		 * Pobiera główny obiekt Usług.
		 */
		async getServicesList() {
			const result:Tree[] = [];

			const rsvp = await serverFetch(
				`/${this.apiNamespace}/get-filter-list-new.ofer?includeLocations=${String(
					!this.services.locations.length,
				)}&matchingFilter.idCustomer=${this.customerId}`,
			) as {
				actionErrors?:string[],
				contactPersons:ContactPersonBackend[],
				customer:{ companyName:string }[]
				filterGroups:FilterGroup[],
				kam:ContactPersonBackend[],
			};

			if (rsvp?.actionErrors?.length) {
				document.location.href = '/';

				return undefined;
			}

			if (!this.services.locations.length) {
				this.services.locations = await this.growTree();
			}

			this.services.contactPersons = [...this.getContactPersons(
				rsvp.contactPersons,
			)];

			if (this.isAdminModule) {
				this.companyName = rsvp.customer[0].companyName;
			}

			this.services.operators = this.getOperators(rsvp.kam);

			this.clearStatsCount();

			for (const filterGroup of rsvp.filterGroups) {
				if (!filterGroup.filters.length) {
					continue;
				}

				if (!filterGroup.active) {
					this.services.stats.hasInactiveFilterGroups = true;
				}

				const notUndefined = <TValue>(value: TValue|undefined): value is TValue => value !== undefined;
				const filters = filterGroup.filters.map((filter: FilterBackend) => {
					const processed = this.process({ filterDetails: filter });
					if (processed) {
						this.statsCount(processed);
					}
					return processed || undefined;
				}).filter(notUndefined);

				const automaticFilter = filters.find(({ type }) => type === 'automatic');
				let automaticHasErrors = false;
				if (automaticFilter && automaticFilter.automaticRecommendations) {
					const automatic = automaticFilter.automaticRecommendations;
					automaticHasErrors = automatic.notEnoughBalance || automatic.needsModeration || !automatic.isCategoryVerified;
				}

				const group = {
					name: filterGroup.categoryName,
					categoryName: filterGroup.categoryName,
					categoryParentName: filterGroup.filters[0].categoryListForView.split(' / ')[0],
					categoryId: filterGroup.idCategory,
					isActive: filterGroup.active,
					isInitiallyActive: filterGroup.active,
					automatic: {
						has: filterGroup.apEnabled,
						canBeEnabled: filterGroup.apCanBeEnabled,
						hasErrors: automaticHasErrors,
					},
					quickContact: {
						has: filterGroup.skEnabled,
						canBeEnabled: true,
					},
					isHidden: false,
					isApEnabled: filterGroup.apEnabled,
					isSkEnabled: filterGroup.skEnabled,
					isVerified: filterGroup.filters[0].filterCategories[0].verified as boolean,
					filters,
					userActivityStats: {} as ServicesUserActivityStats,
					isPricingEnabled: filterGroup.isPricingEnabled,
				} as Tree;
				result.push(group);
			}

			this.services.tree = [...result];

			this.groupLimitRecalculate();

			this.invalidStats = this.services.stats.needsModeration || this.services.stats.notEnoughBalance;

			return rsvp;
		},

		/**
		 * Przekształca typ filtra z postaci używanej w backendzie na tą używaną
		 * we frontendzie i vice versa
		 */
		getItemType({
			type,
			reverse = false,
		}: {
						type: string;
						reverse?: boolean;
				}) {
			const back = ['mgFltrTypeNotif', 'mgFltrTypeSk', 'mgFltrTypeIm'];
			const front = ['free', 'quick', 'automatic'];
			const result = (reverse ? back : front)[(reverse ? front : back).indexOf(type)];

			this.itemType = result;

			return result;
		},

		/**
		 * Przekształca typ filtra na postać widoczną dla użytkownika
		 * @param type Typ filtra
		 */
		processItemLabel(type: string) {
			let typeLabel = '';

			switch (type) {
			case 'free':
				typeLabel = 'Dopasowanie';
				break;
			case 'quick':
				typeLabel = 'Szybki kontakt';
				break;
			case 'automatic':
				typeLabel = 'Polecanie';
				break;
			default:
				break;
			}

			return { typeLabel };
		},

		/**
		 * Przekształca nazwę kategorii z postaci backendowej zawierającą pełną
		 * ścieżkę `Rodzic / Kategoria` na samą nazwę kategorii oraz usuwa z niej
		 * znaki specjalne. Dodatkowo, jeżeli kategoria nie jest liściem, dodaj
		 * do niej tekst `[BRANCH]`
		 * @param backendFilter Filtr z którego pobierana jest kategoria
		 */
		processCategoryName(backendFilter:FilterBackend) {
			const { categoryListForView, category, isLeaf } = backendFilter;
			let formattedCategoryName = categoryListForView;

			formattedCategoryName = formattedCategoryName.split('<br/>').join(', ');

			if (formattedCategoryName.indexOf(' / ') !== -1) {
				const split = formattedCategoryName.split(' / ');
				formattedCategoryName = split[1] === 'Pozostałe' ? formattedCategoryName.replace(' / ', ' › ') : split[1];
			}

			if (formattedCategoryName === '' && category) {
				formattedCategoryName = category.name;
			}

			if (!isLeaf) {
				formattedCategoryName = `[BRANCH] ${formattedCategoryName}`;
			}

			return formattedCategoryName;
		},

		/**
		 * Przekształca 9-ciocyfrowy numer telefonu na grupę po trzy cyfry.
		 * W innym przypadku pozostawia go bez zmian.
		 * @param phoneNumber Numer telefonu
		*/
		formatPhoneNumber(phoneNumber: string) {
			const formattedPhoneNumber = `${phoneNumber}`;

			return formattedPhoneNumber.length !== 9
				? formattedPhoneNumber
				: formattedPhoneNumber.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3');
		},

		/**
		 * Przekształca tablicę zaznaczonych lokalizację na łańcuch
		 * znaków w postaci `Województwo (12 powiatów), Województwo`
		 * @param locations Tablica identyfikatorów kategorii
		 */
		processSelectedLocationsLabel(locations:string[]) {
			const allLocations = JSON.parse(JSON.stringify(this.services.locations));

			const result = [];
			const poland:Record<string, number> = {};
			const foreign = [];

			for (const index of locations) {
				if (index.startsWith('locationPl')) {
					// eslint-disable-next-line guard-for-in
					for (const allLocationIndex in allLocations) {
						if (index === allLocationIndex) {
							poland[allLocations[allLocationIndex].name] = -1;

							continue;
						}

						if (allLocations[allLocationIndex].items[index]) {
							poland[allLocations[allLocationIndex].name] = poland[allLocations[allLocationIndex].name]
								? poland[allLocations[allLocationIndex].name] + 1
								: 1;

							continue;
						}
					}
					continue;
				}

				foreign.push(allLocations.locationForeign.items[index].name);
			}

			// eslint-disable-next-line guard-for-in
			for (const index in poland) {
				const item = poland[index];

				result.unshift(
					item === -1
						? index
						: `${index} (${this.Format.PluralForm(
							item,
							['powiat', 'powiaty', 'powiatów'],
							true,
						)})`,
				);
			}

			result.push(...foreign);
			return result.join(', ');
		},

		/**
		 * Pobiera ustawioną osobę kontaktową z podanego filtru lub pierwszą
		 * lepszą z obiektu usług.
		 * @param backendFilter Filtr z którego pobierana jest osoba kontaktowa
		 */
		processContactPerson(backendFilter:FilterBackend) : ContactPerson {
			const contactPerson = this.services.contactPersons.find((person: ContactPerson) => person.id === backendFilter.idCustomerContactUser) || this.services.contactPersons[0];

			return {
				id: backendFilter.idCustomerContactUser,
				contactPersonId: backendFilter.idCustomerContactUser,
				fullName: contactPerson?.fullName || '',
				email: contactPerson?.email || '',
				phone: contactPerson?.phone || '',
			};
		},

		/**
		 * Przekształca informacje o wielkości filtra na łańcuch znaków.
		 * @param backendFilter Filtr do przekształcenia
		 */
		processSizeLabel(backendFilter:FilterBackend) {
			return backendFilter.sizeUnit
				? `Wielkość ${
					backendFilter.sizeFrom ? `od ${backendFilter.sizeFrom}` : ''
				} ${
					backendFilter.sizeTo ? `do ${backendFilter.sizeTo}` : ''
				} ${backendFilter.sizeUnit}`
				: '';
		},

		/**
		 * Przekształca informacje o filtrze z postaci backendowej do frontendowej.
		 * @param filterDetails Filtr do przekształcenia
		 * @param cloneType Czy mamy sklonować dany filtr? (Ustawić `id`)
		 */
		process({ filterDetails, cloneType = '' }: { filterDetails: FilterBackend, cloneType?: string }) : Filter|undefined {
			// Sprawdź, w jakiej postaci przyszedł filtr.
			// Prawdopodobnie można to uprościć.
			const backendFilter = filterDetails.filter
				? filterDetails.filter[0] || filterDetails.filter
				: filterDetails;

			// Zignoruj filtry, które nie mają kategorii
			if (!backendFilter.filterCategories.length) {
				return undefined;
			}

			if (cloneType) {
				backendFilter.id = 0;
				backendFilter.idType = this.getItemType({
					type: cloneType,
					reverse: true,
				});
			}

			const type = this.getItemType({ type: backendFilter.idType });
			const locationsIds = backendFilter.elementId ? backendFilter.filterLocations.map(
				({ idLocation }) => idLocation,
			) : [];

			const isActive = backendFilter.idStatus === 'mgFltrStatActive' || backendFilter.idStatus === 'mgFltrStatCalcOnly';

			const emptySizeFilter = {
				unit: '',
				minValue: 0,
				minLimit: 0,
				maxValue: 0,
				maxLimit: 1000000,
			} as SizeFilter;

			const result = {
				id: backendFilter.id,
				category: {
					categoryId: backendFilter.filterCategories[0].idCategory,
					name: this.processCategoryName(backendFilter),
					parentId: '',
					isGeneraled: '',
				},
				type,
				...this.processItemLabel(type),
				sizeLabel: this.processSizeLabel(backendFilter),
				automaticRecommendations: this.processAsAutomatic(backendFilter),
				promoted: {},
				contactPrice: backendFilter.contactPrice,
				contactPerson: this.processContactPerson(backendFilter),
				isActive,
				idType: backendFilter.idType,
				isInitiallyActive: isActive,
				isFrozen: backendFilter.idType === 'mgFltrTypeSk',
				locations: {} as Record<string, GeoTreeLocation>,
				location: {
					mode: backendFilter.locationMode || 'select',
					selected: locationsIds,
					radius: backendFilter.radius > 0 ? backendFilter.radius : 50,
					center: null, // this.customer.locationCenter,
					labels: this.processSelectedLocationsLabel(locationsIds),
				},
				quickContactSmsNumber: backendFilter.smsNumber,
				hideAdvancedSettings: true,
				minContactPrice: backendFilter.minContactPrice,
				maxContactPrice: backendFilter.maxContactPrice,
				upcommingPriceChange: backendFilter.upcommingPriceChange,
				isApSuspended: backendFilter.isApSuspended,
				tsSuspendedEnd: backendFilter.tsSuspendedEnd,
				modified: new Date(backendFilter.tsModified || backendFilter.tsCreated),
				filterTypes: {
					size: backendFilter.sizeUnit ? {
						unit: backendFilter.sizeUnit.toUpperCase(),
						minValue: backendFilter.sizeFrom,
						minLimit: 0,
						maxValue: backendFilter.sizeTo,
						maxLimit: 1000000,
					} as SizeFilter : emptySizeFilter,
					age: (backendFilter.usedFrom || backendFilter.usedTo) && (backendFilter.usedFrom > -1 || backendFilter.usedTo > -1) ? {
						unit: 'YEARS',
						minValue: backendFilter.usedFrom,
						minLimit: 0,
						maxValue: backendFilter.usedTo,
						maxLimit: 100,
					} as SizeFilter : emptySizeFilter,
					discounted: backendFilter.notificationWithOutlet,
					standardRfps: backendFilter.standardRfps,
					discountedRfps: backendFilter.outletRfps,
					companies: backendFilter.companies,
					individuals: backendFilter.individuals,
					frequency: backendFilter.idNotificationMode,
					contractors: true,
					investors: true,
					subcontractors: true,
					notConfirmedBySms: backendFilter.notConfirmedSmsRfps,
					hideNotConfirmedBySms: backendFilter.hideNotConfirmedBySms,
				},
				mpo: backendFilter.mpo,
				idCustomerContactUser: backendFilter.idCustomerContactUser,
				isClone: cloneType,
				notEnoughPoints: backendFilter.notEnoughPoints,
				finalMarkBelowApThreshold: backendFilter.finalMarkBelowApThreshold,
				weeklyLimitReached: backendFilter.weeklyLimitReached,
				apValidated: backendFilter.apValidated,
			};

			this.processResult = { ...result };
			return result;
		},

		/**
		 * Przekształca informacje o ustawieniach Polecania filtra z postaci
		 * backendowej do frontendowej.
		 * @param backendFilter Filtr do przekształcenia.
		 */
		processAsAutomatic(backendFilter: FilterBackend) : AutomaticRecommendationsSettings {
			const price = backendFilter.contactPrice || backendFilter.contactPrice === 0 ? backendFilter.contactPrice : backendFilter.categoryDefaultPrice;

			return {
				canBeEnabled: backendFilter.canEnableAP,
				isCategoryValid: backendFilter.isCategoryInAP,
				isCategoryVerified: backendFilter.filterCategories[0].verified,
				needsModeration: backendFilter.imNeedsModeration,
				enabledBy: [backendFilter.apEnabledOperator, backendFilter.tsApEnabled],
				disabledBy: [backendFilter.apDisabledOperator, backendFilter.tsApDisabled],
				weeklyLimit: backendFilter.imWeeklyLimit && backendFilter.idType === 'mgFltrTypeIm' ? backendFilter.imWeeklyLimit : price * 10,
				weeklyLimitMin: Math.round(price * 2),
				weeklyLimitMax: price * 50,
				smsNumber: {
					enabled: backendFilter.imSmsNotification,
					number: backendFilter.customer
						? this.formatPhoneNumber(backendFilter.customer.contactPersonPhone)
						: '',
					isValid: backendFilter.phoneCheck === 'Komórkowy poprawny',
				},
				recommendedClientsCount: backendFilter.recommendClientsCount,
				contactedClientsCount: backendFilter.contactClientsCount,
				remainingPoints: backendFilter.remainPoints,
				notEnoughBalance: backendFilter.notEnoughBalance,
			};
		},

		/**
		 * Resetuje statystyki dla wszystkich filtrów.
		 */
		clearStatsCount() {
			this.services.stats = {
				hasInactiveFilterGroups: false,
				notEnoughBalance: false,
				setLimitSum: 0,
				needsModeration: false,
				withAutomatic: false,
				withQuick: false,
				inactiveCount: 0,
				activeGroupCount: 0,
				hasGroupedCategories: false,
				groupLimitCheck: '',
			};
		},

		/**
		 * Zlicza statystyki dla wszystkich filtrów: czy istnieją usługi dodatkowe,
		 * czy limity punktów dla Polecania nie przekraczają stanu konta, etc.
		 * @param filter Kolejny zliczany filtr z listy
		 */
		statsCount(filter : Filter) {
			const { stats } = this.services;
			const { isActive, type, automaticRecommendations } = filter;

			if (!isActive) { stats.inactiveCount++; }

			if (type === 'quick') { stats.withQuick = true; }

			if (automaticRecommendations && type === 'automatic') {
				stats.withAutomatic = true;

				if (isActive) {
					stats.setLimitSum += automaticRecommendations.weeklyLimit;
				}

				if (automaticRecommendations.needsModeration) {
					stats.needsModeration = true;
				}

				if (automaticRecommendations.notEnoughBalance) {
					stats.notEnoughBalance = true;
				}
			}
		},

		/**
		 * Pobiera listę usług oraz aktualizuje listę grup filtrów.
		 */
		async loadData() {
			this.isLoading = true;
			await this.getServicesList();
			this.isLoading = false;

			if (document.location.hash) {
				const filterId = Number(document.location.hash.substr(1));
				console.log('document', filterId);

				for (let category of this.services.tree) {
					const filter = category.filters.find((filter) => filter.id === filterId);
					if (filter) {
						const categoryId = category.categoryId;
						const el = document.querySelector(`[data-category-id="${categoryId}"]`);
						if (el) { el.click(); }
						break;
					}
				}
			}
		},

		/**
		 * Przenosi użytkownika na ekran dodawania nowej usługi.
		 */
		goToCreate() {
			this.currentPage = 'Create';
			this.isNewCategory = true;
			this.pricingCategoryId = 0;

			this.$router.push({ name: 'FilterCreate' });
		},

		/**
		 * Ustawia aktualnie edytowany filtr (po wywołaniu metody `editFilter`
		 * w komponencie `FilterList`) w polu `selectedFilters` obiektu `services`
		 * @param filterToEdit Wybrany filtr do edycji
		 */
		onSelectedFiltersChange(filterToEdit: Filter) {
			this.services.selectedFilters = [{ ...filterToEdit }];
		},

		/**
		 * Usuwa pojedynczy filtr z grupy, a następnie usuwa go z listy usług.
		 * @param filterToRemove Filtr do usunięcia
		 */
		async removeFilter(filterToRemove: FilterEntry) {
			await fetch(
				`/${this.apiNamespace}/remove-filter.ofer?matchingFilter.idCustomer=${this.customerId}&matchingFilter.id=${filterToRemove.id}&matchingFilter.categories=${filterToRemove.categoryId}`,
			);

			const categoryIndex = this.services.tree.findIndex((category) => category.categoryId === filterToRemove.categoryId);
			const filterIndex = this.services.tree[categoryIndex].filters.findIndex((filter) => filter.id === filterToRemove.id);

			this.services.tree[categoryIndex].filters.splice(filterIndex, 1);

			this.filterToRemove = {
				id: 0,
				type: '',
				categoryId: 0,
			};

			this.$router.back();

			this.Toast.Show(`${filterToRemove.type === 'quick' ? 'Szybki Kontakt został usunięty' : 'Polecanie zostało usunięte'}`);
		},

		/**
		 * Usuwa grupy filtrów, a następnie odświeża listę wszystkich usług.
		 * @param groups Tablica z grupami do usunięcia
		 */
		async removeGroups(groups:{ isSelected:boolean, categoryId:number}[]) {
			for (const group of groups) {
				const { categoryId } = group;

				await fetch(
					`/${this.apiNamespace}/remove-filter-group.ofer?matchingFilter.idCustomer=${this.customerId}&catId=${categoryId}`,
				);
			}

			this.getServicesList();

			this.groupsToRemove = [];
			this.Toast.Show(`${groups.length > 1 ? 'Kategorie zostały usunięte' : 'Kategoria została usunięta'}`);

			this.$router.push({ name: 'Services' });
		},
		backWithNoSaving(pushRouterBack: boolean, source = 'button') {
			if (source !== 'button') {
				this.displaySaveChanges = false;
				return;
			}
			this.isFilterDirty = false;
			this.displaySaveChanges = false;

			if (pushRouterBack) {
				this.$router.back();
			}
		},
		cancelRemoveModal() {
			this.filterToRemove = {
				type: '',
				id: 0,
				categoryId: 0,
			};

			this.$router.back();
		},
		cancelRemoveGroupModal() {
			this.groupsToRemove.length = 0;

			this.$router.back();
		},
	},
};
</script>
